/**
 * Unit type for layer data values.
 * - 'temperature': Thermal data in Fahrenheit
 * - 'height': Elevation/lidar data (displayed as feet and inches)
 * - 'inch': Raw inch values (displayed with " suffix)
 * - 'percent': Percentage values
 * - 'disbond': Binary bonded/disbonded state
 */
export type DataUnit = 'temperature' | 'height' | 'inch' | 'percent' | 'disbond'

export interface KVDataLayerConfig {
  /** The prefix used for this layer (A-Z/a-z, case-sensitive) */
  prefix: string

  name: string

  type: 'rgb' | 'visual' | 'raw'

  /** For raw layers: decode range for the layer values */
  decodeRange?: {
    min_f: number
    max_f: number
  }

  description?: string

  /** Indicates this is a custom user-saved layer with filter presets */
  isCustom?: boolean

  /** For custom layers: unique identifier for UI/hotbar purposes */
  customId?: string

  /** For custom raw layers: saved visible range filter */
  savedVisibleRange?: {
    min_f: number
    max_f: number
  }

  /** For custom raw layers: saved gradient control points */
  savedGradient?: {
    control_points: Array<{
      temp_f: number
      color_rgba: [number, number, number, number]
    }>
  }

  /** For custom layers: saved opacity */
  savedOpacity?: number

  /** UV channel index for this layer */
  uvIndex: number

  /** What unit the raw data represents */
  dataUnit?: DataUnit
}

export type KVDataLayersConfigJSON = {
  layers: Array<{
    prefix: string
    name: string
    type: 'rgb' | 'visual' | 'raw'
    decodeRange?: {
      min_f: number
      max_f: number
    }
    description?: string
    isCustom?: boolean
    customId?: string
    savedVisibleRange?: {
      min_f: number
      max_f: number
    }
    savedGradient?: {
      control_points: Array<{
        temp_f: number
        color_rgba: [number, number, number, number]
      }>
    }
    savedOpacity?: number
    uvIndex?: number
    dataUnit?: DataUnit
  }>
  layerSwitchingEnabled: boolean
  isMultiObjectModel: boolean
}

export class KVDataLayersConfig {
  layers: KVDataLayerConfig[]
  layerSwitchingEnabled: boolean
  isMultiObjectModel: boolean

  constructor() {
    this.layers = [
      {
        prefix: 'RGB',
        name: 'RGB',
        type: 'rgb',
        description: 'Standard RGB visual layer',
        uvIndex: 0,
      },
    ]
    this.layerSwitchingEnabled = false
    this.isMultiObjectModel = false
  }

  static fromJSON(
    json: KVDataLayersConfigJSON | undefined
  ): KVDataLayersConfig {
    const config = new KVDataLayersConfig()

    if (!json) {
      return config
    }

    config.layerSwitchingEnabled = json.layerSwitchingEnabled ?? false
    config.isMultiObjectModel = json.isMultiObjectModel ?? false

    if (json.layers && Array.isArray(json.layers)) {
      config.layers = json.layers.map((layer) => ({
        prefix: layer.prefix,
        name: layer.name,
        type: layer.type,
        decodeRange: layer.decodeRange,
        description: layer.description,
        isCustom: layer.isCustom,
        customId: layer.customId,
        savedVisibleRange: layer.savedVisibleRange,
        savedGradient: layer.savedGradient,
        savedOpacity: layer.savedOpacity,
        uvIndex: layer.uvIndex ?? 0,
        dataUnit:
          layer.dataUnit ??
          (layer.type === 'raw' && layer.prefix.startsWith('Elevation')
            ? 'height'
            : undefined),
      }))
    }

    if (config.layers.length === 0) {
      config.layers = [
        {
          prefix: 'RGB',
          name: 'RGB',
          type: 'rgb',
          description: 'Standard RGB visual layer',
          uvIndex: 0,
        },
      ]
    }

    return config
  }

  static toJSON(config: KVDataLayersConfig): KVDataLayersConfigJSON {
    return {
      layers: config.layers.map((layer) => ({
        prefix: layer.prefix,
        name: layer.name,
        type: layer.type,
        decodeRange: layer.decodeRange,
        description: layer.description,
        isCustom: layer.isCustom,
        customId: layer.customId,
        savedVisibleRange: layer.savedVisibleRange,
        savedGradient: layer.savedGradient,
        savedOpacity: layer.savedOpacity,
        uvIndex: layer.uvIndex,
        dataUnit: layer.dataUnit,
      })),
      layerSwitchingEnabled: config.layerSwitchingEnabled,
      isMultiObjectModel: config.isMultiObjectModel,
    }
  }

  getFirstLayer(): KVDataLayerConfig {
    return this.layers[0]
  }

  getAvailablePrefixes(): string[] {
    return this.layers.map((layer) => layer.prefix)
  }

  getLayerByPrefix(prefix: string): KVDataLayerConfig | undefined {
    return this.layers.find((layer) => layer.prefix === prefix)
  }

  getLayerByPrefixAndUvIndex(
    prefix: string,
    uvIndex: number
  ): KVDataLayerConfig | undefined {
    return this.layers.find(
      (layer) => layer.prefix === prefix && layer.uvIndex === uvIndex
    )
  }

  getLayersByType(type: 'rgb' | 'visual' | 'raw'): KVDataLayerConfig[] {
    return this.layers.filter((layer) => layer.type === type)
  }
}
